import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  signup: (data) => api.post('/auth/signup', data),
  me: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
};

// Dashboard
export const dashboardAPI = {
  getStats: (params = {}) => api.get('/dashboard/stats', { params }),
  getRecentActivity: (limit = 10) => api.get(`/dashboard/recent-activity?limit=${limit}`),
  getTrend: (days = 30) => api.get(`/dashboard/trend?days=${days}`),
  getTrendByType: (days = 30, recordType = null) => {
    const params = { days };
    if (recordType) params.record_type = recordType;
    return api.get('/dashboard/inspection-trend-by-type', { params });
  },
  getTopInspectors: (days = 30) => api.get(`/dashboard/top-inspectors?days=${days}`),
  getHighRiskCylinders: (limit = 5) => api.get(`/dashboard/high-risk-cylinders?limit=${limit}`),
  quickVerify: (q) => api.get(`/verify/search?q=${encodeURIComponent(q)}`),
  // Inspector Performance
  getInspectorPerformance: (inspectorId = null, days = 30) => {
    const params = { days };
    if (inspectorId) params.inspector_id = inspectorId;
    return api.get('/dashboard/inspector-performance', { params });
  },
  getAllInspectorsComparison: (days = 30) => api.get(`/dashboard/all-inspectors-comparison?days=${days}`),
  getInspectorMonthlyTrend: (inspectorId = null, months = 6) => {
    const params = { months };
    if (inspectorId) params.inspector_id = inspectorId;
    return api.get('/dashboard/inspector-monthly-trend', { params });
  },
  // Expiry Forecast
  getExpiringInspections: (window = '30') => api.get('/dashboard/expiring-inspections', { params: { window } }),
  exportExpiringInspections: (window = '30', fmt = 'excel') => api.get('/dashboard/expiring-inspections/export', {
    params: { window, fmt },
    responseType: 'blob',
  }),
};

// Records
export const recordsAPI = {
  getAll: (params = {}) => api.get('/records', { params }),
  getSummary: (params = {}) => api.get('/records/summary', { params }),
  getById: (id) => api.get(`/records/${id}`),
  getInspections: (id) => api.get(`/records/${id}/inspections`),
  getAudit: (id) => api.get(`/records/${id}/audit`),
  create: (data) => api.post('/records', data),
  update: (id, data, reason) => api.put(`/records/${id}`, { ...data, reason }),
  delete: (id, reason) => api.delete(`/records/${id}`, { data: { reason } }),
  searchExact: (query) => api.get('/records/search-exact', { params: { query } }),
};

// Inspections
export const inspectionsAPI = {
  getAll: (params = {}) => api.get('/inspections', { params }),
  getById: (id) => api.get(`/inspections/${id}`),
  getCylinders: (id) => api.get(`/inspections/${id}/cylinders`),
  getAttachments: (id) => api.get(`/inspections/${id}/attachments`),
  getAllAttachments: (id) => api.get(`/inspections/${id}/all-attachments`),
  getTestReport: (id, reportType) => api.get(`/inspections/${id}/test-reports/${reportType}`),
  replaceTestReport: (id, reportType, data) => api.put(`/inspections/${id}/test-reports/${reportType}`, data),
  getAudit: (id) => api.get(`/inspections/${id}/audit`),
  create: (data) => api.post('/inspections', data),
  update: (id, data, reason) => api.put(`/inspections/${id}`, { ...data, reason }),
  updateDraft: (id, data) => api.put(`/inspections/${id}/draft`, data),
  updateCustomer: (id, data) => api.patch(`/inspections/${id}/customer`, data),
  delete: (id, reason) => api.delete(`/inspections/${id}`, { data: { reason } }),
  getCertificate: (id) => api.get(`/inspections/${id}/certificate`, { responseType: 'blob' }),
  getQRSticker: (id) => api.get(`/inspections/${id}/qr-sticker`, { responseType: 'blob' }),
  getQRImage: (id) => api.get(`/inspections/${id}/qr-image`, { responseType: 'blob' }),
  recertify: (id, data) => api.post(`/inspections/${id}/recertify`, data),
};

// Inspectors
export const inspectorsAPI = {
  getAll: (params = {}) => api.get('/inspectors', { params }),
  getStats: (id) => api.get(`/inspectors/${id}/stats`),
  getInspections: (id, params = {}) => api.get(`/inspectors/${id}/inspections`, { params }),
};

// Regulators
export const regulatorsAPI = {
  getAll: () => api.get('/regulators'),
  create: (data) => api.post('/regulators', data),
};

// Admin
export const adminAPI = {
  getPendingUsers: () => api.get('/admin/pending-users'),
  getAllUsers: (params = {}) => api.get('/admin/users', { params }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  createUser: (data) => api.post('/admin/users', data),
  approveUser: (id) => api.post(`/admin/approve-user/${id}`),
  rejectUser: (id, reason) => api.post(`/admin/reject-user/${id}`, { reason }),
  updateUser: (id, data, reason) => api.put(`/admin/users/${id}`, { updates: data, reason }),
  deleteUser: (id, reason) => api.delete(`/admin/users/${id}`, { data: { reason } }),
  getAuditLogs: (params = {}) => api.get('/admin/audit-logs', { params }),
  getConfig: () => api.get('/admin/config'),
  updateConfig: (data) => api.put('/admin/config', data),
  // Deleted Records
  getDeletedRecords: (params = {}) => api.get('/admin/deleted-records', { params }),
  restoreDeletedRecord: (id, entityType) => api.post(`/admin/deleted-records/${id}/restore?entity_type=${entityType}`),
  permanentDeleteRecord: (id, entityType, reason) => api.delete(`/admin/deleted-records/${id}/permanent?entity_type=${entityType}`, { data: { reason } }),
};

// Organizations
export const organizationsAPI = {
  getAll: () => api.get('/organizations'),
  create: (data) => api.post('/organizations', data),
};

// Public
export const publicAPI = {
  verify: (token) => api.get(`/verify/${token}`),
};

// Validation - Duplicate checks
export const validationAPI = {
  checkRegistration: (registrationNumber, excludeRecordId = null) => {
    const params = excludeRecordId ? { exclude_record_id: excludeRecordId } : {};
    return api.get(`/validate/registration/${encodeURIComponent(registrationNumber)}`, { params });
  },
  checkStorageSerial: (serialNumber, excludeRecordId = null) => {
    const params = excludeRecordId ? { exclude_record_id: excludeRecordId } : {};
    return api.get(`/validate/storage-serial/${encodeURIComponent(serialNumber)}`, { params });
  },
  checkCylinder: (serialNumber, excludeRecordId = null) => {
    const params = excludeRecordId ? { exclude_record_id: excludeRecordId } : {};
    return api.get(`/validate/cylinder/${encodeURIComponent(serialNumber)}`, { params });
  },
  checkCylindersBatch: (serials, excludeRecordId = null, excludeCylinderIds = null) => api.post('/validate/cylinders-batch', {
    serials,
    exclude_record_id: excludeRecordId,
    exclude_cylinder_ids: excludeCylinderIds,
  }),
};

// Reports
export const reportsAPI = {
  exportInspections: (params = {}) => api.get('/reports/inspections', { params }),
  exportInspectionsPDF: (params = {}) => api.get('/reports/inspections', { 
    params: { ...params, format: 'pdf' },
    responseType: 'blob'
  }),
  exportInspectionsExcel: (params = {}) => api.get('/reports/inspections', { 
    params: { ...params, format: 'excel' },
    responseType: 'blob'
  }),
};

// Workshops
export const workshopsAPI = {
  getAll: (params = {}) => api.get('/workshops', { params }),
  getById: (id) => api.get(`/workshops/${id}`),
  getStats: () => api.get('/workshops/stats'),
  create: (data) => api.post('/workshops', data),
  update: (id, data) => api.put(`/workshops/${id}`, data),
  activate: (id) => api.patch(`/workshops/${id}/activate`),
  deactivate: (id) => api.patch(`/workshops/${id}/deactivate`),
  delete: (id) => api.delete(`/workshops/${id}`),
  syncFromHistory: () => api.post('/workshops/sync'),
};

// Seed
export const seedAPI = {
  seed: () => api.post('/seed'),
};

export default api;
